/**
 * "طباعة السجل الطبي" — the patient's whole record as a printable document.
 *
 * Shared by the patient's own record screen and by the doctor's live view, so
 * both produce the identical document. The patient could take nothing away
 * before this; the doctor had nothing to attach to a referral.
 *
 * The visits and prescriptions are fetched here rather than passed in, because
 * the two callers reach the patient from different directions — the patient
 * screen already holds them, the live view holds only a booking. Passing them
 * when you have them skips the fetch.
 */

import { useState } from 'react';
import { Button } from './index';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { bookingApi, prescriptionApi, medicalRecordApi } from '../../api/endpoints';
import { printMedicalRecord } from '../../services/printPrescription';

export function PrintMedicalRecordButton({
  patientId,
  patientName,
  patientCode = '',
  patientPhone = '',
  bookings = null,
  prescriptions = null,
  externalRecords = null,
  doctor = null,
  className = '',
  variant = 'ghost',
  label = 'طباعة السجل الطبي',
  icon = 'printer',
}) {
  const { session } = useAuth();
  const { error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      // Only fetch what the caller did not already have.
      const [visits, scripts, external] = await Promise.all([
        bookings ?? (patientId ? bookingApi.list({ patientId }) : []),
        prescriptions ?? (patientId ? prescriptionApi.listForPatient(patientId) : []),
        // The doctor's copy fetches these too. The backend only serves them to
        // the patient, an admin, or a doctor that patient has booked with, so a
        // 403 here is a legitimate answer rather than a failure — the record
        // still prints, without the external section.
        externalRecords ??
          (patientId ? medicalRecordApi.list(patientId).catch(() => []) : []),
      ]);

      if ((visits || []).length === 0 && (scripts || []).length === 0 && (external || []).length === 0) {
        toastError('لا توجد بيانات في السجل الطبي لهذا المريض بعد.');
        return;
      }

      await printMedicalRecord({
        patient: { name: patientName, code: patientCode, phone: patientPhone },
        bookings: visits || [],
        prescriptions: scripts || [],
        externalRecords: external || [],
        doctor,
        issuedBy: session?.name || '',
      });
    } catch (err) {
      toastError(err.message || 'تعذر تجهيز السجل الطبي');
    } finally {
      setBusy(false);
    }
  };

  // A record needs someone to be about. A walk-in with no account has no
  // history to assemble, and an empty document would be worse than no button.
  if (!patientId || !patientName) return null;

  return (
    <Button
      variant={variant}
      icon={icon}
      loading={busy}
      onClick={handleClick}
      className={className}
      title="افتح السجل الطبي كاملاً للطباعة أو الحفظ PDF"
    >
      {label}
    </Button>
  );
}

export default PrintMedicalRecordButton;
